import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import { CustomDropdown } from "../../../componentes/dropdown/CustomDropdown";
import { useExportToExcel } from "../../../componentes/table/useExportToExcel";
import Button from "../../../componentes/button/button";
import CardIndicador from "../Shared/CardIndicador";
import ContasAPagarService from "../../../services/resource/contasAPagarService";
import { formatarCnpjCpf, formatarData, formatarMoeda } from "../../../utils/solicitacoes/formatters";
import { hojeIso } from "../../../utils/solicitacoes/datas";

const service = new ContasAPagarService();

function isoDaquiA(dias) {
    const data = new Date();
    data.setDate(data.getDate() + dias);
    return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
}

const INPUT_CLASS = "h-9 px-3 text-sm border border-input rounded-lg bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15";

// Exportação para Excel continua plana (uma linha por parcela), com todas as colunas.
const COLUNAS_EXPORTACAO = [
    { key: "filialNome", label: "Filial" },
    { key: "fornecedorNome", label: "Fornecedor" },
    { key: "fornecedorFantasia", label: "Fantasia" },
    { key: "fornecedorDocumentoTxt", label: "CNPJ/CPF" },
    { key: "tipoDocumento", label: "Tipo" },
    { key: "numeroDocumento", label: "Documento/NF" },
    { key: "titulo", label: "Parcela" },
    { key: "dataEntrada", label: "Data de Entrada", format: "date" },
    { key: "dataEmissaoNf", label: "Data da NF", format: "date" },
    { key: "vencimento", label: "Vencimento", format: "date" },
    { key: "valorParcela", label: "Valor da Parcela", format: "currency" },
    { key: "valorDocumento", label: "Valor do Documento", format: "currency" },
    { key: "valorBruto", label: "Valor Bruto", format: "currency" },
    { key: "competencia", label: "Competência" },
    { key: "codigo", label: "Código Fortes" },
];

// Relatório "Contas a Pagar em Aberto": parcelas ainda não quitadas no Fortes, filtradas pelo
// vencimento (ver FortesAGService#buscarContasPagarEmAberto no backend).
export default function ContasPagarEmAberto() {
    const [vencimentoInicial, setVencimentoInicial] = useState(hojeIso());
    const [vencimentoFinal, setVencimentoFinal] = useState(isoDaquiA(7));
    const [parcelas, setParcelas] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [parcelaSelecionada, setParcelaSelecionada] = useState(null);
    const [busca, setBusca] = useState("");
    const [filial, setFilial] = useState(null);
    const { exportToExcel } = useExportToExcel();

    function pesquisar() {
        if (!vencimentoInicial || !vencimentoFinal) {
            toast.error("Informe o período de vencimento.");
            return;
        }
        if (vencimentoInicial > vencimentoFinal) {
            toast.error("A data inicial não pode ser maior que a final.");
            return;
        }
        setCarregando(true);
        service.listarEmAbertoNoFortes(vencimentoInicial, vencimentoFinal)
            .then(res => setParcelas(res.data))
            .catch(error => {
                console.error("Erro ao consultar contas a pagar em aberto no Fortes", error);
                toast.error(error.response?.data?.message || "Não foi possível consultar o Fortes.");
            })
            .finally(() => setCarregando(false));
    }

    useEffect(() => {
        pesquisar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const linhas = useMemo(() => parcelas.map((p, i) => ({
        ...p,
        chave: `${p.codigo}-${p.sequencial ?? i}`,
        fornecedorDocumentoTxt: p.fornecedorDocumento ? formatarCnpjCpf(p.fornecedorDocumento) : "-",
    })), [parcelas]);

    const opcoesFilial = useMemo(() => {
        const nomes = new Set(linhas.map(l => l.filialNome).filter(Boolean));
        return Array.from(nomes).sort().map(nome => ({ value: nome, label: nome }));
    }, [linhas]);

    // Busca rápida e filial filtram no próprio navegador (sem nova consulta ao Fortes).
    const linhasFiltradas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        const termoDigitos = termo.replace(/\D/g, "");
        return linhas.filter(l => {
            if (filial && l.filialNome !== filial) return false;
            if (!termo) return true;
            const texto = `${l.fornecedorNome || ""} ${l.fornecedorFantasia || ""} ${l.numeroDocumento || ""}`.toLowerCase();
            return texto.includes(termo) || (termoDigitos.length >= 3 && (l.fornecedorDocumento || "").includes(termoDigitos));
        });
    }, [linhas, busca, filial]);

    const hoje = hojeIso();
    const total = linhasFiltradas.reduce((soma, p) => soma + (Number(p.valorParcela) || 0), 0);
    const vencidas = linhasFiltradas.filter(p => p.vencimento && p.vencimento < hoje);
    const totalVencidas = vencidas.reduce((soma, p) => soma + (Number(p.valorParcela) || 0), 0);
    const fornecedores = new Set(linhasFiltradas.map(p => p.fornecedorDocumento || p.fornecedorNome)).size;

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <CardIndicador label="Parcelas em aberto" value={linhasFiltradas.length} icon="fa fa-file-invoice" iconClassName="bg-primary/10 text-primary" loading={carregando} />
                <CardIndicador label="Total em aberto" value={formatarMoeda(total)} icon="fa fa-sack-dollar" iconClassName="bg-amber-100 text-amber-700" loading={carregando} />
                <CardIndicador label={`Vencidas (${vencidas.length})`} value={formatarMoeda(totalVencidas)} icon="fa fa-exclamation-triangle" iconClassName="bg-destructive/10 text-destructive" loading={carregando} />
                <CardIndicador label="Fornecedores" value={fornecedores} icon="fa fa-building" iconClassName="bg-success/10 text-success" loading={carregando} />
            </div>

            <div className="bg-card border border-border rounded-lg shadow-card p-4 flex items-end gap-3 flex-wrap">
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Vencimento de <span className="text-destructive">*</span></label>
                    <input type="date" value={vencimentoInicial} onChange={e => setVencimentoInicial(e.target.value)} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Vencimento até <span className="text-destructive">*</span></label>
                    <input type="date" value={vencimentoFinal} onChange={e => setVencimentoFinal(e.target.value)} className={INPUT_CLASS} />
                </div>
                <Button variant="blue" label="Pesquisar" iconLeft="fa fa-search" loading={carregando} onClick={pesquisar} />
                <div className="flex flex-col gap-1.5 min-w-[240px] flex-1">
                    <label className="text-sm font-medium text-foreground/80">Buscar</label>
                    <input
                        type="text"
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                        placeholder="Fornecedor, fantasia, CNPJ ou documento"
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="flex flex-col gap-1.5 min-w-[200px]">
                    <label className="text-sm font-medium text-foreground/80">Filial</label>
                    <CustomDropdown value={filial} onChange={e => setFilial(e.value)} options={opcoesFilial} placeholder="Todas" showClear filter />
                </div>
                <Button
                    variant="outline"
                    label="Exportar Excel"
                    iconLeft="fa fa-file-excel"
                    disabled={linhasFiltradas.length === 0}
                    onClick={() => exportToExcel(linhasFiltradas, COLUNAS_EXPORTACAO, "contas_a_pagar_em_aberto")}
                />
            </div>

            <TabelaPorFornecedor
                parcelas={linhasFiltradas}
                carregando={carregando}
                hoje={hoje}
                onSelecionar={setParcelaSelecionada}
            />

            <Dialog
                header={parcelaSelecionada ? `${parcelaSelecionada.fornecedorNome || "Fornecedor"} — ${parcelaSelecionada.numeroDocumento || parcelaSelecionada.codigo}` : ""}
                visible={!!parcelaSelecionada}
                onHide={() => setParcelaSelecionada(null)}
                blockScroll
                maximizable
                className="w-[95vw] max-w-5xl"
            >
                {parcelaSelecionada && <DetalheParcela parcela={parcelaSelecionada} hoje={hoje} />}
            </Dialog>
        </div>
    );
}

// Tabela no formato do relatório do Fortes: agrupada por fornecedor, com o total de cada um e o total geral.
function TabelaPorFornecedor({ parcelas, carregando, hoje, onSelecionar }) {
    const grupos = useMemo(() => {
        const porFornecedor = new Map();
        parcelas.forEach(p => {
            const chave = p.fornecedorDocumento || p.fornecedorNome || "—";
            if (!porFornecedor.has(chave)) {
                porFornecedor.set(chave, { chave, nome: p.fornecedorNome, fantasia: p.fornecedorFantasia, documento: p.fornecedorDocumento, parcelas: [], total: 0 });
            }
            const grupo = porFornecedor.get(chave);
            grupo.parcelas.push(p);
            grupo.total += Number(p.valorParcela) || 0;
        });
        const porVencimento = (a, b) => (a.vencimento || "").localeCompare(b.vencimento || "")
            || (a.numeroDocumento || "").localeCompare(b.numeroDocumento || "")
            || (a.titulo || "").localeCompare(b.titulo || "", "pt-BR", { numeric: true });
        return Array.from(porFornecedor.values())
            .map(g => ({ ...g, parcelas: [...g.parcelas].sort(porVencimento) }))
            .sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR"));
    }, [parcelas]);

    const totalGeral = grupos.reduce((soma, g) => soma + g.total, 0);
    const celula = "px-3 py-1.5 whitespace-nowrap";

    return (
        <div className="bg-card border border-border rounded-lg shadow-card overflow-hidden">
            <div className="overflow-auto max-h-[70vh]">
                <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-table-header text-table-header-fg">
                        <tr className="text-left">
                            <th className={`${celula} font-semibold`}>Vencimento</th>
                            <th className={`${celula} font-semibold`}>Filial</th>
                            <th className={`${celula} font-semibold`}>Documento/NF</th>
                            <th className={`${celula} font-semibold text-center`}>Parcela</th>
                            <th className={`${celula} font-semibold`}>Entrada</th>
                            <th className={`${celula} font-semibold text-right`}>Valor da Parcela</th>
                        </tr>
                    </thead>
                    <tbody>
                        {carregando ? (
                            <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">
                                <i className="fa fa-spinner fa-spin mr-2" /> Consultando o Fortes...
                            </td></tr>
                        ) : grupos.length === 0 ? (
                            <tr><td colSpan={6} className="px-3 py-8 text-muted-foreground">Nenhuma conta em aberto no período.</td></tr>
                        ) : grupos.map(g => (
                            <FragmentoFornecedor key={g.chave} grupo={g} hoje={hoje} celula={celula} onSelecionar={onSelecionar} />
                        ))}
                    </tbody>
                    {!carregando && grupos.length > 0 && (
                        <tfoot className="sticky bottom-0 bg-muted">
                            <tr className="border-t-2 border-border font-bold text-foreground">
                                <td colSpan={5} className={celula}>Total Geral ({parcelas.length} parcela(s) de {grupos.length} fornecedor(es))</td>
                                <td className={`${celula} text-right`}>{formatarMoeda(totalGeral)}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}

function FragmentoFornecedor({ grupo, hoje, celula, onSelecionar }) {
    return (
        <>
            <tr className="bg-muted/60 border-t border-border">
                <td colSpan={6} className={`${celula} pt-3`}>
                    <span className="text-muted-foreground font-semibold mr-2">Fornecedor:</span>
                    <span className="font-bold text-foreground">{grupo.nome || "—"}</span>
                    {grupo.fantasia && grupo.fantasia !== grupo.nome && <span className="text-muted-foreground"> · {grupo.fantasia}</span>}
                    {grupo.documento && <span className="text-muted-foreground"> · {formatarCnpjCpf(grupo.documento)}</span>}
                </td>
            </tr>
            {grupo.parcelas.map(p => {
                const vencida = p.vencimento && p.vencimento < hoje;
                return (
                    <tr key={p.chave} onClick={() => onSelecionar(p)} className="border-t border-border/50 hover:bg-table-row-hover cursor-pointer">
                        <td className={`${celula} pl-6 ${vencida ? "text-destructive font-semibold" : ""}`}>{formatarData(p.vencimento)}</td>
                        <td className={celula}>{p.filialNome || "-"}</td>
                        <td className={celula}>{p.numeroDocumento || "-"}{p.tipoDocumento ? <span className="text-muted-foreground"> ({p.tipoDocumento})</span> : null}</td>
                        <td className={`${celula} text-center`}>{p.titulo || "-"}</td>
                        <td className={celula}>{formatarData(p.dataEntrada)}</td>
                        <td className={`${celula} text-right`}>{formatarMoeda(p.valorParcela)}</td>
                    </tr>
                );
            })}
            <tr className="border-t border-border font-semibold text-foreground">
                <td colSpan={5} className={celula + ' font-base'}></td>
                <td className={`${celula} text-right`}>{formatarMoeda(grupo.total)}</td>
            </tr>
        </>
    );
}

// Todos os dados da parcela clicada + o lançamento completo no Fortes (todas as parcelas e o rateio),
// buscado ao abrir.
function DetalheParcela({ parcela, hoje }) {
    const [lancamento, setLancamento] = useState(null);
    const [carregando, setCarregando] = useState(true);

    useEffect(() => {
        let cancelado = false;
        setCarregando(true);
        setLancamento(null);
        service.buscarLancamentoNoFortes(parcela.codigo)
            .then(res => { if (!cancelado) setLancamento(res.data); })
            .catch(() => { if (!cancelado) toast.error("Não foi possível carregar o lançamento no Fortes."); })
            .finally(() => { if (!cancelado) setCarregando(false); });
        return () => { cancelado = true; };
    }, [parcela.codigo]);

    const vencida = parcela.vencimento && parcela.vencimento < hoje;

    return (
        <div className="flex flex-col gap-4 pt-2">
            <Secao titulo="Parcela em aberto" extra={vencida
                ? <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-destructive/10 text-destructive">Vencida</span>
                : <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-700">A vencer</span>}>
                <Info label="Parcela" valor={parcela.titulo} />
                <Info label="Sequencial" valor={parcela.sequencial} />
                <Info label="Vencimento" valor={formatarData(parcela.vencimento)} />
                <Info label="Vencimento original" valor={formatarData(parcela.vencimentoOriginal)} />
                <Info label="Previsão de pagamento" valor={formatarData(parcela.previsaoPagamento)} />
                <Info label="Valor da parcela" valor={formatarMoeda(parcela.valorParcela)} destaque />
            </Secao>

            <Secao titulo="Documento">
                <Info label="Código Fortes" valor={parcela.codigo} />
                <Info label="Documento/NF" valor={parcela.numeroDocumento} />
                <Info label="Tipo" valor={parcela.tipoDocumento} />
                <Info label="Competência" valor={parcela.competencia} />
                <Info label="Data de entrada" valor={formatarData(parcela.dataEntrada)} />
                <Info label="Data da NF" valor={formatarData(parcela.dataEmissaoNf)} />
                <Info label="Valor do documento" valor={formatarMoeda(parcela.valorDocumento)} />
                <Info label="Valor bruto" valor={formatarMoeda(parcela.valorBruto)} />
                <Info label="Filial" valor={[parcela.filialCodigo, parcela.filialNome].filter(Boolean).join(" · ")} />
                {lancamento && <Info label="Conta" valor={lancamento.contaNome} />}
                {lancamento && <Info label="Histórico" valor={lancamento.historico} />}
                {lancamento && <Info label="Centro de custo" valor={lancamento.centroCustoCodigo} />}
                <div className="col-span-2 md:col-span-4"><Info label="Observação" valor={parcela.observacao} /></div>
            </Secao>

            <Secao titulo="Fornecedor">
                <Info label="Nome" valor={parcela.fornecedorNome} />
                <Info label="Fantasia" valor={parcela.fornecedorFantasia} />
                <Info label="CNPJ/CPF" valor={parcela.fornecedorDocumento ? formatarCnpjCpf(parcela.fornecedorDocumento) : null} />
                <Info label="Grupo" valor={parcela.grupoFornecedor} />
            </Secao>

            {carregando ? (
                <p className="text-sm text-muted-foreground"><i className="fa fa-spinner fa-spin mr-1" /> Carregando parcelas e rateio do Fortes...</p>
            ) : lancamento && (
                <>
                    <Tabela
                        titulo="Todas as parcelas do documento"
                        cabecalho={["Seq.", "Título", "Vencimento", "Valor", "Desconto", "Situação"]}
                        linhas={lancamento.parcelas.map(p => [
                            p.sequencial ?? "-", p.numeroTitulo || "-", formatarData(p.data), formatarMoeda(p.valor), formatarMoeda(p.desconto),
                            p.dataCancelamento ? `Cancelada em ${formatarData(p.dataCancelamento)}` : p.quitado ? "Paga" : "Em aberto",
                        ])}
                        destacar={i => lancamento.parcelas[i].sequencial === parcela.sequencial}
                    />
                    <Tabela
                        titulo="Rateio"
                        cabecalho={["Filial", "Centro de Custo", "Conta", "Valor", "Conta Contábil", "Nº Solicitação", "Obs."]}
                        linhas={lancamento.rateio.map(r => [
                            r.filialNome || r.filialCodigo || "-",
                            [r.centroCustoCodigo, r.centroCustoNome].filter(Boolean).join(" · ") || "-",
                            [r.contaCodigo, r.contaNome].filter(Boolean).join(" · ") || "-",
                            formatarMoeda(r.valor), r.contaContabil || "-", r.numeroSolicitacao || "-", r.observacao || "-",
                        ])}
                    />
                </>
            )}
        </div>
    );
}

function Secao({ titulo, extra, children }) {
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <h4 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">{titulo}</h4>
                {extra}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm rounded-lg bg-muted px-4 py-3">{children}</div>
        </div>
    );
}

function Info({ label, valor, destaque = false }) {
    return (
        <div className="min-w-0">
            <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</span>
            <span className={`block break-words ${destaque ? "text-base font-bold text-foreground" : "text-foreground font-medium"}`}>
                {valor === null || valor === undefined || valor === "" ? "-" : valor}
            </span>
        </div>
    );
}

function Tabela({ titulo, cabecalho, linhas, destacar = () => false }) {
    return (
        <div>
            <h4 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">{titulo}</h4>
            {linhas.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum registro.</p>
            ) : (
                <div className="overflow-x-auto border border-border rounded-md">
                    <table className="w-full text-xs">
                        <thead className="bg-muted">
                            <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                                {cabecalho.map(c => <th key={c} className="px-2 py-1.5 font-semibold">{c}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {linhas.map((linha, i) => (
                                <tr key={i} className={`border-t border-border ${destacar(i) ? "bg-primary/5 font-semibold" : ""}`}>
                                    {linha.map((celula, j) => <td key={j} className="px-2 py-1.5 whitespace-nowrap">{celula}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
